import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EscalationRulesPanel = ({ rules, onCreateRule, onEditRule }) => {
  const getPriorityColor = (priority) => {
    const colorMap = {
      critical: 'error',
      high: 'warning',
      medium: 'primary',
      low: 'success'
    };
    return colorMap?.[priority] || 'primary';
  };

  const getPriorityBg = (priority) => {
    const bgMap = {
      critical: 'bg-error/10 text-error border-error/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      medium: 'bg-primary/10 text-primary border-primary/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return bgMap?.[priority] || 'bg-primary/10 text-primary border-primary/20';
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      payment_failure: 'Payment Failure',
      equipment_issue: 'Equipment Issue',
      capacity_warning: 'Capacity Warning',
      safety_alert: 'Safety Alert',
      maintenance_required: 'Maintenance Required'
    };
    return labels?.[type] || type;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      payment_failure: 'CreditCard',
      equipment_issue: 'Wrench',
      capacity_warning: 'Users',
      safety_alert: 'Shield',
      maintenance_required: 'Tool'
    };
    return icons?.[type] || 'Bell';
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Escalation Rules</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure automatic routing based on alert type, severity, and staff availability
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={onCreateRule}
        >
          Create Rule
        </Button>
      </div>

      {/* Rules Grid */}
      {rules?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Icon name="Settings" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Escalation Rules</h3>
          <p className="text-sm text-muted-foreground mb-4">Create your first escalation rule to automate alert routing</p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={onCreateRule}
          >
            Create Rule
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rules?.map((rule) => (
            <div
              key={rule?.id}
              className="border border-border rounded-lg p-4 hover:shadow-md transition-all"
            >
              {/* Rule Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getPriorityBg(rule?.priority)}`}>
                    <Icon name={getAlertTypeIcon(rule?.alert_type)} size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">
                      {getAlertTypeLabel(rule?.alert_type)}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBg(rule?.priority)}`}>
                        {rule?.priority?.toUpperCase()}
                      </span>
                      {rule?.is_active ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onEditRule(rule)}
                  className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                >
                  <Icon name="Edit2" size={16} />
                </button>
              </div>

              {/* Rule Details */}
              <div className="space-y-3">
                {/* Target Roles */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Target Roles</p>
                  <div className="flex flex-wrap gap-2">
                    {rule?.target_roles?.map((role, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name={rule?.send_sms ? 'Check' : 'X'} size={14} className={rule?.send_sms ? 'text-success' : 'text-error'} />
                    <span>SMS</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name={rule?.send_email ? 'Check' : 'X'} size={14} className={rule?.send_email ? 'text-success' : 'text-error'} />
                    <span>Email</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name={rule?.send_push ? 'Check' : 'X'} size={14} className={rule?.send_push ? 'text-success' : 'text-error'} />
                    <span>Push</span>
                  </div>
                </div>

                {/* Message Preview */}
                {rule?.message_template && (
                  <div className="p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Message Template</p>
                    <p className="text-sm text-foreground line-clamp-2">{rule?.message_template}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EscalationRulesPanel;